import { useState } from 'react';
import {
  Card, Icon, IconButton, ModalDialog, Hyperlink,
} from '@openedx/paragon';
import {
  RiCalendarTodoLine,
  RiFullscreenLine,
  RiCalendarCloseLine,
  RiBookOpenLine,
  RiFileTextLine,
  RiCheckboxCircleLine,
  RiLinksLine,
  RiTimeLine,
  RiLockLine,
} from '@remixicon/react';
import { useIntl } from '@edx/frontend-platform/i18n';
import { useCalendarEvents } from '../../../gql';
import messages from './messages';

const iconForDateType = (dateType) => {
  switch (dateType) {
    case 'assignment-due-date':
      return RiFileTextLine;
    case 'subsection-release-date':
      return RiTimeLine;
    case 'course-start-date':
    case 'course-end-date':
      return RiBookOpenLine;
    case 'certificate-available-date':
      return RiCheckboxCircleLine;
    default:
      return RiLinksLine;
  }
};

const formatDate = (value, locale) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleDateString(locale || undefined, { day: '2-digit', month: 'short' });
};

const formatTime = (value, locale) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  return d.toLocaleTimeString(locale || undefined, { hour: 'numeric', minute: '2-digit' });
};

const formatFullDate = (value, locale) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return '';
  }
  const datePart = d.toLocaleDateString(locale || undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  const timePart = d.toLocaleTimeString(locale || undefined, { hour: 'numeric', minute: '2-digit' });
  return `${datePart} · ${timePart}`;
};

// Course-author-defined grading types are free text; map the common defaults to
// translatable labels and fall back to the raw server string for custom names.
const ASSIGNMENT_BADGE_KEYS = {
  homework: 'assignmentHomework',
  exam: 'assignmentExam',
  quiz: 'assignmentQuiz',
  lab: 'assignmentLab',
  project: 'assignmentProject',
  'midterm exam': 'assignmentMidterm',
  'final exam': 'assignmentFinal',
  practice: 'assignmentPractice',
};

const getAssignmentBadge = (type, intl) => {
  if (!type) {
    return '';
  }
  const key = ASSIGNMENT_BADGE_KEYS[type.toLowerCase()];
  return key ? intl.formatMessage(messages[key]) : type;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const groupEvents = (events) => {
  const today = startOfDay(new Date());
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfterTomorrow = new Date(today);
  dayAfterTomorrow.setDate(dayAfterTomorrow.getDate() + 2);
  const endOfWeek = new Date(today);
  endOfWeek.setDate(endOfWeek.getDate() + 7);

  const groups = {
    today: [],
    tomorrow: [],
    thisWeek: [],
    later: [],
  };

  events.forEach((event) => {
    const start = new Date(event.start);
    if (Number.isNaN(start.getTime())) {
      groups.later.push(event);
      return;
    }
    const startDay = startOfDay(start);
    if (startDay.getTime() === today.getTime()) {
      groups.today.push(event);
    } else if (startDay.getTime() === tomorrow.getTime()) {
      groups.tomorrow.push(event);
    } else if (startDay >= dayAfterTomorrow && startDay < endOfWeek) {
      groups.thisWeek.push(event);
    } else {
      groups.later.push(event);
    }
  });

  return groups;
};

// Whole-day difference from today; null when there is no valid date. Drives the
// "Due in N days" line (A6) and the "Ends in N days" course badge (D1).
const relativeDateInfo = (value, dateType, intl) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return null;
  }
  const days = Math.round((startOfDay(d) - startOfDay(new Date())) / 86400000);

  if (dateType === 'course-end-date') {
    if (days < 0) {
      return { text: intl.formatMessage(messages.scheduleOverdue), tone: 'overdue' };
    }
    return {
      text: intl.formatMessage(messages.scheduleCourseEndsInDays, { count: days }),
      tone: days <= 3 ? 'soon' : 'normal',
    };
  }

  let text;
  let tone = 'normal';
  if (days < 0) {
    text = intl.formatMessage(messages.scheduleOverdue);
    tone = 'overdue';
  } else if (days === 0) {
    text = intl.formatMessage(messages.scheduleDueToday);
    tone = 'soon';
  } else if (days === 1) {
    text = intl.formatMessage(messages.scheduleDueTomorrow);
    tone = 'soon';
  } else {
    text = intl.formatMessage(messages.scheduleDueInDays, { count: days });
    if (days <= 3) {
      tone = 'soon';
    }
  }
  return { text, tone };
};

// The course end date renders as a pill badge (like the assignment-type badge)
// instead of a relative-date line: amber when ending within 3 days, else blue.
const endsBadge = (event, intl) => {
  if (event.date_type !== 'course-end-date' || event.isRelease) {
    return null;
  }
  const rel = relativeDateInfo(event.start, event.date_type, intl);
  if (!rel) {
    return null;
  }
  return { key: 'ends', variant: rel.tone === 'soon' ? 'warning' : 'info', label: rel.text };
};

// Status badges (success/warning/info) layered alongside the assignment_type
// (danger) badge in the modal. Release items read "Opens …" instead.
const statusBadges = (event, intl) => {
  const badges = [];
  if (event.isRelease) {
    return badges;
  }
  const ends = endsBadge(event, intl);
  if (ends) {
    badges.push(ends);
  }
  if (event.learnerHasAccess === false) {
    badges.push({ key: 'locked', variant: 'warning', label: intl.formatMessage(messages.scheduleStatusLocked) });
  }
  if (event.hasOverride === true) {
    badges.push({ key: 'extended', variant: 'info', label: intl.formatMessage(messages.scheduleStatusExtended) });
  }
  return badges;
};

const ScheduleCard = () => {
  const intl = useIntl();
  const { locale } = intl;
  const { events, loading, error } = useCalendarEvents(true);
  const [isOpen, setIsOpen] = useState(false);

  const upcoming = events.slice(0, 3);
  const grouped = groupEvents(events);
  const groupSections = [
    { key: 'today', label: intl.formatMessage(messages.scheduleGroupToday), items: grouped.today },
    { key: 'tomorrow', label: intl.formatMessage(messages.scheduleGroupTomorrow), items: grouped.tomorrow },
    { key: 'thisWeek', label: intl.formatMessage(messages.scheduleGroupThisWeek), items: grouped.thisWeek },
    { key: 'later', label: intl.formatMessage(messages.scheduleGroupLater), items: grouped.later },
  ].filter((section) => section.items.length > 0);

  const renderEventItem = (event, index) => {
    const itemKey = `${event.title}-${event.start}-${index}`;
    const locked = event.learnerHasAccess === false;
    const EventIcon = locked ? RiLockLine : iconForDateType(event.date_type);
    const rel = relativeDateInfo(event.start, event.date_type, intl);
    const badges = statusBadges(event, intl);
    return (
      <li className={`schedule-modal__item${locked ? ' schedule-modal__item--locked' : ''}`} key={itemKey}>
        <div className="schedule-modal__itemIcon">
          <Icon src={EventIcon} size="sm" />
        </div>
        <div className="schedule-modal__itemBody">
          <div className="schedule-modal__itemTitleRow">
            <span className="schedule-modal__itemTitle">{event.title}</span>
            {event.assignment_type && (
              <span className={`schedule-modal__itemBadge${event.complete === true ? ' schedule-modal__itemBadge--success' : ''}`}>
                {getAssignmentBadge(event.assignment_type, intl)}
              </span>
            )}
            {badges.map((badge) => (
              <span
                key={badge.key}
                className={`schedule-modal__itemBadge schedule-modal__itemBadge--${badge.variant}`}
              >
                {badge.label}
              </span>
            ))}
          </div>
          {event.course_display_name && event.course_display_name !== event.title && (
            <div className="schedule-modal__itemCourse">
              {event.course_display_name}
            </div>
          )}
          {event.sectionName && (
            <div className="schedule-modal__itemSection">
              {event.sectionName}
            </div>
          )}
          <div className="schedule-modal__itemMeta">
            {event.isRelease
              ? intl.formatMessage(messages.scheduleOpens, { date: formatFullDate(event.start, locale) })
              : formatFullDate(event.start, locale)}
          </div>
          {rel && !event.isRelease && event.date_type !== 'course-end-date' && (
            <div
              className={`schedule-modal__itemRelative${rel.tone !== 'normal' ? ` schedule-modal__itemRelative--${rel.tone}` : ''}`}
            >
              {rel.text}
            </div>
          )}
          {event.hasOverride && event.overrideReason && (
            <div className="schedule-modal__itemSection">
              {intl.formatMessage(messages.scheduleExtendedReason, { reason: event.overrideReason })}
            </div>
          )}
          {event.description && (
            <div className="schedule-modal__itemDescription">
              {event.description}
            </div>
          )}
          {event.link && (
            <Hyperlink
              destination={event.link}
              target="_blank"
              className="schedule-modal__itemLink"
            >
              {event.ctaLabel || intl.formatMessage(messages.scheduleViewDetails)}
            </Hyperlink>
          )}
        </div>
      </li>
    );
  };

  const renderBody = () => {
    if (loading) {
      return (
        <div className="schedule-card__empty">
          <div className="schedule-card__emptyText">
            {intl.formatMessage(messages.scheduleLoading)}
          </div>
        </div>
      );
    }

    if (error) {
      return (
        <div className="schedule-card__empty">
          <div className="schedule-card__emptyText">
            {intl.formatMessage(messages.scheduleError)}
          </div>
        </div>
      );
    }

    if (upcoming.length === 0) {
      return (
        <div className="schedule-card__empty">
          <div className="schedule-card__emptyIcon">
            <Icon src={RiCalendarCloseLine} size="md" />
          </div>
          <div className="schedule-card__emptyText">
            {intl.formatMessage(messages.noSessionsScheduled)}
          </div>
        </div>
      );
    }

    return (
      <ul className="schedule-card__list">
        {upcoming.map((event, index) => {
          const itemKey = `${event.title}-${event.start}-${index}`;
          const locked = event.learnerHasAccess === false;
          const EventIcon = locked ? RiLockLine : iconForDateType(event.date_type);
          const badges = statusBadges(event, intl);
          return (
            <li className={`schedule-card__item${locked ? ' schedule-card__item--locked' : ''}`} key={itemKey}>
              <div className="schedule-card__itemIcon">
                <Icon src={EventIcon} size="sm" />
              </div>
              <div className="schedule-card__itemBody">
                <div className="schedule-card__itemTitle" title={event.title}>
                  {event.title}
                </div>
                <div className="schedule-card__itemMeta">
                  <span>{formatDate(event.start, locale)}</span>
                  <span>·</span>
                  <span>{formatTime(event.start, locale)}</span>
                </div>
              </div>
              {(event.assignment_type || badges.length > 0) && (
                <div className="schedule-card__itemBadges">
                  {event.assignment_type && (
                    <span className={`schedule-card__itemBadge${event.complete === true ? ' schedule-card__itemBadge--success' : ''}`}>
                      {getAssignmentBadge(event.assignment_type, intl)}
                    </span>
                  )}
                  {badges.map((badge) => (
                    <span
                      key={badge.key}
                      className={`schedule-card__itemBadge schedule-card__itemBadge--${badge.variant}`}
                    >
                      {badge.label}
                    </span>
                  ))}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    );
  };

  return (
    <Card className="schedule-card" variant="light">
      <div className="schedule-card__header">
        <div className="schedule-card__headerLeft">
          <Icon className="schedule-card__headerIcon" src={RiCalendarTodoLine} size="sm" />
          <span className="schedule-card__headerLabel">{intl.formatMessage(messages.schedule)}</span>
        </div>
        <IconButton
          src={RiFullscreenLine}
          variant="ghost-tertiary"
          size="sm"
          alt={intl.formatMessage(messages.scheduleExpandAlt)}
          onClick={() => setIsOpen(true)}
        />
      </div>

      {renderBody()}

      <ModalDialog
        title={intl.formatMessage(messages.scheduleModalTitle)}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        size="md"
        hasCloseButton
        isFullscreenOnMobile
      >
        <ModalDialog.Header>
          <ModalDialog.Title>
            {intl.formatMessage(messages.scheduleModalTitle)}
          </ModalDialog.Title>
        </ModalDialog.Header>
        <ModalDialog.Body className="schedule-modal__body">
          {events.length === 0 ? (
            <div className="schedule-modal__empty">
              {intl.formatMessage(messages.noSessionsScheduled)}
            </div>
          ) : (
            groupSections.map((section) => (
              <section className="schedule-modal__section" key={section.key}>
                {groupSections.length > 1 && (
                  <h3 className="schedule-modal__sectionTitle">{section.label}</h3>
                )}
                <ul className="schedule-modal__list">
                  {section.items.map((event, index) => renderEventItem(event, index))}
                </ul>
              </section>
            ))
          )}
        </ModalDialog.Body>
      </ModalDialog>
    </Card>
  );
};

export default ScheduleCard;
